import React, { useState, useEffect } from 'react';
import {
	Palette,
	Edit3,
	Check,
	X,
	MessageSquare,
	Paintbrush,
	Hash,
	Target,
	MapPin,
	FileText,
	Search,
	TrendingUp,
	Star,
} from 'lucide-react';
import Card from '../../../components/ui/card';
import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';

const BrandingCard = ({ client, clientDataHook }) => {
	const { updateCard, isCardEditMode, toggleCardEditMode, cancelCardEdit, isOnboarding, refreshData, isLoading } = clientDataHook;
	const cardName = 'branding';
	const isEditing = isOnboarding || isCardEditMode(cardName);
	
	// Refresh data when component mounts (for onboarding)
	useEffect(() => {
		if (isOnboarding && refreshData) {
			console.log('🟦 BrandingCard - Component mounted, refreshing data');
			refreshData();
		}
	}, [isOnboarding]);

	// Form state for onboarding mode
	const [formValues, setFormValues] = useState({
		tagline: client?.tagline || '',
		brandingColors: client?.brandingColors || '',
		hashtags: client?.hashtags || '',
		keywords: client?.keywords || '',
		specialties: client?.specialties || '',
		nicheMarkets: client?.nicheMarkets || '',
		uniqueness: client?.uniqueness || '',
		serviceAreas: client?.serviceAreas || '',
		additionalNotes: client?.additionalNotes || ''
	});

	// Update form values when client data changes
	useEffect(() => {
		setFormValues({
			tagline: client?.tagline || '',
			brandingColors: client?.brandingColors || '',
			hashtags: client?.hashtags || '',
			keywords: client?.keywords || '',
			specialties: client?.specialties || '',
			nicheMarkets: client?.nicheMarkets || '',
			uniqueness: client?.uniqueness || '',
			serviceAreas: client?.serviceAreas || '',
			additionalNotes: client?.additionalNotes || ''
		});
	}, [client]);

	const fields = [
		{
			key: 'tagline',
			label: 'Do you have a tag line?',
			type: 'text',
			value: client?.tagline || '',
			icon: MessageSquare,
			placeholder: 'Your professional tagline...',
		},
		{
			key: 'brandingColors',
			label: 'Branding Colors & Fonts',
			type: 'textarea',
			value: client?.brandingColors || '',
			icon: Paintbrush,
			placeholder: 'Colors: Primary #3B82F6, Secondary #10B981\nFonts: Heading: Inter, Body: Open Sans',
		},
		{
			key: 'hashtags',
			label: "Do you have hashtags you'd like us to use?",
			type: 'text',
			value: client?.hashtags || '',
			icon: Hash,
			placeholder: '#YourBrand #RealEstate #LocalExpert',
		},
		{
			key: 'keywords',
			label: 'Keywords associated with your brand',
			type: 'text',
			value: client?.keywords || '',
			icon: Search,
			placeholder: 'SEO keywords for search optimization...',
		},
		{
			key: 'specialties',
			label: 'What are your specialties?',
			type: 'textarea',
			value: client?.specialties || '',
			icon: Target,
			placeholder: 'Luxury homes, first-time buyers, investment properties...',
		},
		{
			key: 'nicheMarkets',
			label: 'Niche markets you are an expert in',
			type: 'textarea',
			value: client?.nicheMarkets || '',
			icon: TrendingUp,
			placeholder: 'Waterfront properties, historic homes, eco-friendly builds...',
		},
		{
			key: 'uniqueness',
			label: 'What makes you unique in real estate?',
			type: 'textarea',
			value: client?.uniqueness || '',
			icon: Star,
			placeholder: 'What sets you apart from other agents...',
		},
		{
			key: 'serviceAreas',
			label: 'What areas do you serve?',
			type: 'textarea',
			value: client?.serviceAreas || '',
			icon: MapPin,
			placeholder: 'Cities, neighborhoods, or regions you serve...',
		},
		{
			key: 'additionalNotes',
			label: 'Additional branding information',
			type: 'textarea',
			value: client?.additionalNotes || '',
			icon: FileText,
			placeholder: 'Any other branding details you would like us to know...',
		},
	];

	
	// Handle form field changes for onboarding
	const handleFormFieldChange = (fieldKey, value) => {
		setFormValues(prev => ({
			...prev,
			[fieldKey]: value
		}));
	};
	
	// Handle save for onboarding mode
	const handleOnboardingSave = async () => {
		const success = await updateCard(formValues);
		return success;
	};

	// Handle save for profile mode (card-wide editing)
	const handleSave = async () => {
		const success = await updateCard(formValues);
		if (success) {
			toggleCardEditMode(cardName); // Exit edit mode
		}
	};

	// Handle cancel for profile mode (card-wide editing)
	const handleCancel = () => {
		// Reset form values to original client data
		setFormValues({
			tagline: client?.tagline || '',
			brandingColors: client?.brandingColors || '',
			hashtags: client?.hashtags || '',
			keywords: client?.keywords || '',
			specialties: client?.specialties || '',
			nicheMarkets: client?.nicheMarkets || '',
			uniqueness: client?.uniqueness || '',
			serviceAreas: client?.serviceAreas || '',
			additionalNotes: client?.additionalNotes || ''
		});
		cancelCardEdit(cardName);
	};

	return (
		<Card>
			{!isOnboarding && (
				<Card.Header className='flex flex-row items-center justify-center space-y-0 pb-4'>
					<div className='space-y-1 text-center'>
						<Card.Title className='flex items-center gap-2 text-lg font-montserrat-medium text-gray-900 justify-center'>
							<Palette className='h-5 w-5' />
							Your Branding
						</Card.Title>
						<Card.Description className='text-sm text-gray-600'>
							Your brand identity, colors, specialties, and service areas.
						</Card.Description>
					</div>
					{!isEditing && (
						<Button
							size='sm'
							variant='outline'
							onClick={() => toggleCardEditMode(cardName)}
							className='border-gray-300 text-gray-600 hover:bg-gray-50 ml-4'>
							<Edit3 className='h-4 w-4 mr-2' />
							Edit
						</Button>
					)}
				</Card.Header>
			)}
			<Card.Content className='space-y-4'>
				{fields.map((field) => (
					<div key={field.key} className='space-y-2'>
						<label className='text-sm font-semibold text-gray-800'>
							{field.label}
						</label>
						
						{isEditing ? (
							<div className='space-y-2'>
								{field.type === 'textarea' ? (
									<Textarea
										value={formValues[field.key] || ''}
										onChange={(e) => handleFormFieldChange(field.key, e.target.value)}
										placeholder={field.placeholder}
										className='w-full min-h-[80px]'
									/>
								) : (
									<Input
										type={field.type}
										value={formValues[field.key] || ''}
										onChange={(e) => handleFormFieldChange(field.key, e.target.value)}
										placeholder={field.placeholder}
										className='w-full'
									/>
								)}
							</div>
						) : (
							<div className='flex items-start gap-2'>
								<field.icon className='h-4 w-4 text-gray-500 mt-0.5' />
								<div className='flex-1'>
									<span className='text-sm font-montserrat-regular text-gray-900 whitespace-pre-wrap'>
										{field.value || (
											<span className='text-gray-400 italic'>
												{field.placeholder}
											</span>
										)}
									</span>
								</div>
							</div>
						)}
					</div>
				))}
				
				{/* Save/Cancel buttons when editing in profile mode */}
				{isEditing && !isOnboarding && (
					<div className='flex justify-end gap-3 pt-4 border-t border-gray-200'>
						<Button
							variant='outline'
							onClick={handleCancel}
							className='border-gray-300 text-gray-600 hover:bg-gray-50'>
							<X className='h-4 w-4 mr-2' />
							Cancel
						</Button>
						<Button
							onClick={handleSave}
							className='bg-gray-900 hover:bg-gray-800 text-white'>
							<Check className='h-4 w-4 mr-2' />
							Save Changes
						</Button>
					</div>
				)}
				
				{/* Save button for onboarding */}
				{isOnboarding && (
					<div className='pt-6 mt-6 border-t border-gray-200'>
						<Button
							onClick={handleOnboardingSave}
							className='w-full bg-gray-900 hover:bg-gray-800 text-white'>
							<Check className='h-4 w-4 mr-2' />
							Save & Continue
						</Button>
					</div>
				)}
			</Card.Content>
		</Card>
	);
};

export default BrandingCard;